import { ReactNode, useEffect } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { getUser } from '@frontend/auth-helpers';
import { DEFAULT_CONTEXT_VALUE, useGetContextValues, useNotifications } from '../hooks';
import { TaskCenterContext } from '../types';

const TaskCenterProviderContext = createContext<TaskCenterContext>(DEFAULT_CONTEXT_VALUE);

export const useTaskCenterStore = <T extends (keyof TaskCenterContext)[]>(values: T) => {
  return useContextSelector(TaskCenterProviderContext, (context) =>
    values.reduce((acc, key) => ({ ...acc, [key]: context?.[key] }), {} as Pick<TaskCenterContext, T[number]>)
  );
};

export const TaskCenterProvider = ({ children }: { children: ReactNode }) => {
  const contextValue = useGetContextValues();

  useNotifications({
    onViewNotification: contextValue.onViewNotification,
  });

  useEffect(() => {
    if (!contextValue.isTaskCenterOpen) {
      // whenever we close task center panel, we want to reset the API filters
      contextValue.resetFilters?.();
    }
  }, [contextValue.isTaskCenterOpen]);

  useEffect(() => {
    // Setting this as assignee filter here because getUser returns undefined on the first render
    contextValue.setAssigneeFilter?.(getUser()?.userID ?? '');
  }, [getUser]);

  return <TaskCenterProviderContext.Provider value={contextValue}>{children}</TaskCenterProviderContext.Provider>;
};
